import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { IoClose } from "react-icons/io5";
import { useDispatch } from "react-redux";
import { updateLoanData } from "../../redux/actions/loanAction";
import { toast } from "react-hot-toast";

const extractFileName = (url) => {
  return url.substring(url.lastIndexOf('/') + 1, url.indexOf('?') === -1 ? url.length : url.indexOf('?'));
};

export default function EditLoanPopup({ isOpen, onClose, loanList, onSave }) {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    eligibilityCriteria: "",
    applicationProcess: "",
    contactInformation: "",
    files: [],
  });
  const fileInputRef = useRef(null);
  const [addMoreClicked, setAddMoreClicked] = useState(false);

  useEffect(() => {
    if (loanList) {
      setFormData({
        title: loanList.title || "",
        description: loanList.description || "",
        eligibilityCriteria: loanList.eligibity_criteria || "",
        applicationProcess: loanList.application_process || "",
        contactInformation: loanList.contact_information || "",
        files: loanList.files || [],
      });
    }
  }, [loanList]);

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      [{ font: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      ["bold", "italic", "underline"],
      [{ align: [] }],
      ["clean"],
    ],
  };

  const formats = ["font", "header", "list", "bold", "italic", "underline", "align"];

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleQuillChange = useCallback((name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleFileRemove = (index) => {
    const updatedFiles = [...formData.files];
    updatedFiles.splice(index, 1);
    setFormData((prev) => ({ ...prev, files: updatedFiles }));
  };

  const handleAddFile = () => {
    setAddMoreClicked(true);
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const updatedFiles = [...formData.files, { file: file, existingPath: null }];
    setFormData((prev) => ({ ...prev, files: updatedFiles }));
    setAddMoreClicked(false);
  };

  const stripHtml = useCallback((html) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    const plainDescription = stripHtml(formData.description);
    const plainEligibilityCriteria = stripHtml(formData.eligibilityCriteria);
    const applicationProcess = stripHtml(formData.applicationProcess);
    const contactInformation = stripHtml(formData.contactInformation);

    if (!formData.title?.trim()) {
      toast.error("Loan name is required.");
      return;
    }
    if (!plainDescription.trim()) {
      toast.error("Description is required.");
      return;
    }
    if (!plainEligibilityCriteria.trim()) {
      toast.error("Eligibility criteria is required.");
      return;
    }
    if (!applicationProcess.trim()) {
      toast.error("Application Process are required.");
      return;
    }
    if (!contactInformation.trim()) {
      toast.error("Contact Information are required.");
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append("title", formData.title);
    formDataToSend.append("description", formData.description);
    formDataToSend.append("eligibity_criteria", formData.eligibilityCriteria);
    formDataToSend.append("application_process", formData.applicationProcess);
    formDataToSend.append("contact_information", formData.contactInformation);

    const newFiles = [];
    const existingFiles = [];

    formData.files.forEach((fileData) => {
      if (fileData) {
        if (fileData.file) {
          newFiles.push(fileData.file);
        } else {
          existingFiles.push(extractFileName(fileData));
        }
      }
    });

    newFiles.forEach((file) => {
      formDataToSend.append("files", file);
    });

    formDataToSend.append("existing_files", JSON.stringify(existingFiles));

    try {
      await dispatch(updateLoanData(loanList.id, formDataToSend));
      toast.success("Loan updated successfully!");
      console.log("Calling onSave from EditLoanPopup");
      onSave();
      onClose();
    } catch (error) {
      console.error("Error updating loan:", error);
      toast.error("Failed to update loan.");
    }
  }, [dispatch, loanList, formData, stripHtml, onSave, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed top-0 left-0 z-50 flex items-center justify-center w-full h-full bg-black bg-opacity-50">
      <div className="w-[1169px] p-4 bg-white rounded-lg shadow-lg max-h-[90vh]">
        <div className="flex items-center justify-between mb-6">
          <button
            type="button"
            className="text-gray-500 border rounded-md shadow-md hover:text-gray-700"
            onClick={onClose}
          >
            <IoClose className="w-6 h-6" />
          </button>
        </div>
        <div className="overflow-y-auto max-h-[75vh] p-4">
          <h3 className="mb-4 text-xl font-semibold text-gray-700">Edit Loan Schemes</h3>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-600" htmlFor="title">
                  Title
                </label>
                <input
                  id="title"
                  name="title"
                  type="text"
                  value={formData.title}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none"
                />
              </div>
            </div>

            <div className="mt-6">
              <label className="block mb-1 text-gray-700">Description</label>
              <ReactQuill
                value={formData.description}
                theme="snow"
                modules={modules}
                formats={formats}
                onChange={(value) => handleQuillChange("description", value)}
                className="h-[150px] mb-1"
              />
            </div>

            <div className="mt-12">
              <label className="block mb-1 text-gray-700">Eligibility Criteria</label>
              <ReactQuill
                value={formData.eligibilityCriteria}
                theme="snow"
                modules={modules}
                formats={formats}
                onChange={(value) => handleQuillChange("eligibilityCriteria", value)}
                className="h-[150px] mb-1"
              />
            </div>

            <div className="mt-12">
              <label className="block mb-1 text-gray-700">Application Process</label>
              <ReactQuill
                value={formData.applicationProcess}
                theme="snow"
                modules={modules}
                formats={formats}
                onChange={(value) => handleQuillChange("applicationProcess", value)}
                className="h-[150px] mb-1"
              />
            </div>

            <div className="mt-12">
              <label className="block mb-1 text-gray-700">Contact Information</label>
              <ReactQuill
                value={formData.contactInformation}
                theme="snow"
                modules={modules}
                formats={formats}
                onChange={(value) => handleQuillChange("contactInformation", value)}
                className="h-[150px] mb-1"
              />
            </div>

            <div className="mt-6">
              <label className="block mb-1 text-sm font-medium text-gray-600">Supporting Documents</label>
              <div className="space-y-4">
                {formData.files.map((fileData, index) => {
                  const isNewFile = fileData && fileData.file;
                  const file = isNewFile ? fileData.file : fileData;
                  const fileURL = isNewFile ? URL.createObjectURL(file) : file;

                  return (
                    <div key={index} className="flex items-center space-x-4">
                      <span className="w-full px-4 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-md">
                        {typeof file === 'string' ? extractFileName(file) : file.name}
                      </span>
                      <a
                        href={fileURL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 text-white bg-[#92288C] rounded-md shadow hover:bg-[#6C0B67]"
                      >
                        View
                      </a>
                      <button
                        type="button"
                        className="px-4 py-2 text-white bg-[#92288C] rounded-md shadow hover:bg-[#6C0B67]"
                        onClick={() => handleFileRemove(index)}
                      >
                        Remove
                      </button>
                    </div>
                  );
                })}
              </div>
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <button
                type="button"
                onClick={handleAddFile}
                className="mt-4 text-sm text-[#92288C] hover:underline"
              >
                Add more
              </button>
            </div>

            <div className="flex justify-end mt-6 space-x-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-[#A8AAAE] bg-gray-200 rounded-md hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-white bg-[#92288C] rounded-md hover:bg-[#6C0B67]"
              >
                Update
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}